import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../contexts';
import styled from 'styled-components';
import { 
  ArrowLeft,
  Calendar, 
  MapPin, 
  Package, 
  CheckCircle,
  Clock,
  AlertTriangle,
  Building,
  User,
  TrendingUp,
  BarChart3,
  Download,
  Share,
  Edit,
  Eye,
  ExternalLink
} from 'lucide-react';
import { Project, ProjectCarton } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Card, Title } from '../components/Card';
import { toast } from 'react-hot-toast';

const ProjectDetailContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const ErrorTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  color: ${({ theme }) => theme.colors.error};
  margin: 0;
`;

const ErrorButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const Header = styled.header`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const ProjectInfo = styled.div`
  flex: 1;
`;

const ProjectTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography['3xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-transform: capitalize;
  background: ${({ theme, status }) => {
    switch (status) {
      case 'planning': return theme.colors.warningBackground;
      case 'in-progress': return theme.colors.primaryBackground;
      case 'completed': return theme.colors.successBackground;
      case 'on-hold': return theme.colors.errorBackground;
      default: return theme.colors.gray100;
    }
  }};
  color: ${({ theme, status }) => {
    switch (status) {
      case 'planning': return theme.colors.warning;
      case 'in-progress': return theme.colors.primary;
      case 'completed': return theme.colors.success;
      case 'on-hold': return theme.colors.error;
      default: return theme.colors.textSecondary;
    }
  }};
`;

const ProjectDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.base};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.white};
  color: ${({ theme, variant }) => variant === 'primary' ? theme.colors.white : theme.colors.text};
  border: 1px solid ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primaryDark : theme.colors.gray50};
    transform: translateY(-1px);
  }
`;

const Main = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing[6]};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[6]};
`;

const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
`;

const ProgressCard = styled(Card)`
  padding: ${({ theme }) => theme.spacing[6]};
`;

const ProgressStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ProgressStat = styled.div`
  text-align: center;
`;

const ProgressValue = styled.div`
  font-size: ${({ theme }) => theme.typography['2xl']};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const ProgressLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ProgressBarContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProgressBarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.primaryDark});
  transition: width 0.3s ease;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const QuickActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const QuickActionLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const CartonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const CartonItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const CartonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const CartonIcon = styled.div<{ received: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme, received }) => received ? theme.colors.success : theme.colors.warning};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.white};
`;

const CartonDetails = styled.div`
  flex: 1;
`;

const CartonName = styled.div`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const CartonStatus = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.primaryBackground};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-weight: ${({ theme }) => theme.typography.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const QuickActionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const QuickAction = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};
  text-align: left;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

const QuickActionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`;

const QuickActionContent = styled.div`
  flex: 1;
`;

const QuickActionTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const QuickActionDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProjectInfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ProjectInfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ProjectInfoLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.medium};
`;

const ProjectInfoValue = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-align: right;
`;

const ProjectSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.medium};
`;

const SummaryValue = styled.div`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-align: right;
`;

const StatisticsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.gray50};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primaryBackground};
  color: ${({ theme }) => theme.colors.primary};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { setSelectedProject } = useProject();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('ProjectDetailPage mounted with ID:', projectId);
    const id = projectId || window.location.pathname.split('/').pop();
    if (id) {
      fetchProjectDetails(id);
    } else {
      console.error('No project ID provided');
      setError('No project ID provided');
      setLoading(false);
    }
  }, [projectId]);

  const fetchProjectDetails = async (id: string) => {
    try {
      console.log('Starting fetchProjectDetails...');
      setLoading(true);
      setError(null);
      
      // Fetch detailed project data which includes all information
      console.log('Fetching detailed project data...');
      const projectData = await apiService.getProjectDetailed(id);
      console.log('Project data received:', projectData);
      
      if (!projectData) {
        throw new Error('No project data received');
      }

      // Set project data - this includes all the information we need
      setProject(projectData);
      // Set the project in context so it persists across navigation
      setSelectedProject(projectData);
      console.log('ProjectDetailPage: Project data set in state and context:', projectData);

      // The detailed API already includes carton data in the statistics
      // We don't need to make separate API calls for cartons and stats
      console.log('Project details loaded successfully with all data');
    } catch (error) {
      console.error('Error in fetchProjectDetails:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load project details';
      setError(errorMessage);
      toast.error(errorMessage);
      setTimeout(() => {
        navigate('/projects');
      }, 2000);
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/projects');
  };

  const formatNumber = (num: number) => {
    return isNaN(num) ? 0 : num;
  };

  if (loading) {
    console.log('Rendering loading state');
    return (
      <ProjectDetailContainer>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <LoadingSpinner size="large" text="Loading project details..." />
        </div>
      </ProjectDetailContainer>
    );
  }

  if (error) {
    console.log('Rendering error state:', error);
    return (
      <ProjectDetailContainer>
        <ErrorContainer>
          <ErrorTitle>Error: {error}</ErrorTitle>
          <ErrorButton onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Projects
          </ErrorButton>
        </ErrorContainer>
      </ProjectDetailContainer>
    );
  }

  if (!project) {
    console.log('Rendering not found state');
    return (
      <ProjectDetailContainer>
        <ErrorContainer>
          <ErrorTitle>Project not found</ErrorTitle>
          <ErrorButton onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Projects
          </ErrorButton>
        </ErrorContainer>
      </ProjectDetailContainer>
    );
  }

  console.log('Rendering project details:', project);
  
  // Calculate progress from project statistics
  const progress = project.statistics?.cartons?.total_cartons 
    ? (project.statistics.cartons.received_cartons / project.statistics.cartons.total_cartons) * 100 
    : 0;

  return (
    <ProjectDetailContainer>
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBack}>
            <ArrowLeft size={16} />
            Back to Projects
          </BackButton>

          <ProjectHeader>
            <ProjectInfo>
              <ProjectTitle>{project.code}</ProjectTitle>
              <ProjectMeta>
                <MetaItem>
                  <Building size={16} />
                  {project.customer_name || 'No Customer'}
                </MetaItem>
                {project.project_city && (
                  <MetaItem>
                    <MapPin size={16} />
                    {project.project_city}
                  </MetaItem>
                )}
                {project.start_date && project.end_date && (
                  <MetaItem>
                    <Calendar size={16} />
                    {new Date(project.start_date).toLocaleDateString()} - {new Date(project.end_date).toLocaleDateString()}
                  </MetaItem>
                )}
                <StatusBadge status={project.status}>
                  {project.status.replace('-', ' ')}
                </StatusBadge>
              </ProjectMeta>
              {project.description && (
                <ProjectDescription>
                  {project.description}
                </ProjectDescription>
              )}
            </ProjectInfo>

            <ActionButtons>
              <ActionButton variant="secondary">
                <Share size={16} />
                Share
              </ActionButton>
              <ActionButton variant="secondary">
                <Download size={16} />
                Export
              </ActionButton>
              <ActionButton variant="primary">
                <Edit size={16} />
                Edit Project
              </ActionButton>
            </ActionButtons>
          </ProjectHeader>
        </HeaderContent>
      </Header>

      <Main>
        <ContentGrid>
          <LeftColumn>
            <ProgressCard>
              <CardTitle>Project Progress</CardTitle>
              <ProgressStats>
                <ProgressStat>
                  <ProgressValue>{formatNumber(project.statistics?.cartons?.received_cartons || 0)}</ProgressValue>
                  <ProgressLabel>Received</ProgressLabel>
                </ProgressStat>
                <ProgressStat>
                  <ProgressValue>{formatNumber((project.statistics?.cartons?.total_cartons || 0) - (project.statistics?.cartons?.received_cartons || 0))}</ProgressValue>
                  <ProgressLabel>Pending</ProgressLabel>
                </ProgressStat>
                <ProgressStat>
                  <ProgressValue>{formatNumber(project.statistics?.cartons?.total_cartons || 0)}</ProgressValue>
                  <ProgressLabel>Total</ProgressLabel>
                </ProgressStat>
                <ProgressStat>
                  <ProgressValue>{formatNumber(Math.round(progress))}%</ProgressValue>
                  <ProgressLabel>Complete</ProgressLabel>
                </ProgressStat>
              </ProgressStats>

              <ProgressBarContainer>
                <ProgressBarLabel>
                  <span>Carton Delivery Progress</span>
                  <span>{formatNumber(Math.round(progress))}%</span>
                </ProgressBarLabel>
                <ProgressBar>
                  <ProgressFill progress={progress} />
                </ProgressBar>
              </ProgressBarContainer>
            </ProgressCard>

            <Card>
              <CardTitle>Project Summary</CardTitle>
              <ProjectSummary>
                <SummaryItem>
                  <SummaryLabel>Project ID</SummaryLabel>
                  <SummaryValue>{project.code}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Order ID</SummaryLabel>
                  <SummaryValue>{project.order_id || 'N/A'}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Customer</SummaryLabel>
                  <SummaryValue>{project.customer_name || 'No Customer'}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Status</SummaryLabel>
                  <SummaryValue>
                    <StatusBadge status={project.status}>
                      {project.status.replace('-', ' ')}
                    </StatusBadge>
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Order Date</SummaryLabel>
                  <SummaryValue>
                    {project.order_date ? new Date(project.order_date).toLocaleDateString() : 'N/A'}
                  </SummaryValue>
                </SummaryItem>
                {project.project_city && (
                  <SummaryItem>
                    <SummaryLabel>Location</SummaryLabel>
                    <SummaryValue>{project.project_city}</SummaryValue>
                  </SummaryItem>
                )}
                {project.delivery_address && (
                  <SummaryItem>
                    <SummaryLabel>Delivery Address</SummaryLabel>
                    <SummaryValue>{project.delivery_address}</SummaryValue>
                  </SummaryItem>
                )}
              </ProjectSummary>
            </Card>
          </LeftColumn>

          <RightColumn>
            <Card>
              <CardTitle>Project Statistics</CardTitle>
              <StatisticsGrid>
                <StatCard>
                  <StatIcon>
                    <Package size={20} />
                  </StatIcon>
                  <StatContent>
                    <StatValue>{project.statistics?.cartons?.total_cartons || 0}</StatValue>
                    <StatLabel>Total Cartons</StatLabel>
                  </StatContent>
                </StatCard>
                <StatCard>
                  <StatIcon>
                    <CheckCircle size={20} />
                  </StatIcon>
                  <StatContent>
                    <StatValue>{project.statistics?.cartons?.received_cartons || 0}</StatValue>
                    <StatLabel>Received</StatLabel>
                  </StatContent>
                </StatCard>
                <StatCard>
                  <StatIcon>
                    <Clock size={20} />
                  </StatIcon>
                  <StatContent>
                    <StatValue>{project.statistics?.panels?.total_panels || 0}</StatValue>
                    <StatLabel>Total Panels</StatLabel>
                  </StatContent>
                </StatCard>
                <StatCard>
                  <StatIcon>
                    <TrendingUp size={20} />
                  </StatIcon>
                  <StatContent>
                    <StatValue>{project.statistics?.hardware?.total_hardware || 0}</StatValue>
                    <StatLabel>Hardware Items</StatLabel>
                  </StatContent>
                </StatCard>
              </StatisticsGrid>
            </Card>

            <Card>
              <CardTitle>Quick Actions</CardTitle>
              <QuickActions>
                <QuickActionButton onClick={() => navigate('/cartons')}>
                  <QuickActionIcon>
                    <Package size={20} />
                  </QuickActionIcon>
                  <QuickActionLabel>Manage Cartons</QuickActionLabel>
                </QuickActionButton>

                <QuickActionButton onClick={() => navigate('/cartons/receive')}>
                  <QuickActionIcon>
                    <CheckCircle size={20} />
                  </QuickActionIcon>
                  <QuickActionLabel>Receive Cartons</QuickActionLabel>
                </QuickActionButton>

                <QuickActionButton>
                  <QuickActionIcon>
                    <Download size={20} />
                  </QuickActionIcon>
                  <QuickActionLabel>Reports</QuickActionLabel>
                </QuickActionButton>
              </QuickActions>
            </Card>

            <Card>
              <CardTitle>Project Information</CardTitle>
              <ProjectInfoList>
                <ProjectInfoItem>
                  <ProjectInfoLabel>Project Code</ProjectInfoLabel>
                  <ProjectInfoValue>{project.code}</ProjectInfoValue>
                </ProjectInfoItem>
                <ProjectInfoItem>
                  <ProjectInfoLabel>SO Number</ProjectInfoLabel>
                  <ProjectInfoValue>{project.so_number || 'N/A'}</ProjectInfoValue>
                </ProjectInfoItem>
                <ProjectInfoItem>
                  <ProjectInfoLabel>Order ID</ProjectInfoLabel>
                  <ProjectInfoValue>{project.order_id || 'N/A'}</ProjectInfoValue>
                </ProjectInfoItem>
                {project.partner_name && (
                  <ProjectInfoItem>
                    <ProjectInfoLabel>Partner</ProjectInfoLabel>
                    <ProjectInfoValue>{project.partner_name}</ProjectInfoValue>
                  </ProjectInfoItem>
                )}
                {project.customer_contact && (
                  <ProjectInfoItem>
                    <ProjectInfoLabel>Customer Contact</ProjectInfoLabel>
                    <ProjectInfoValue>{project.customer_contact}</ProjectInfoValue>
                  </ProjectInfoItem>
                )}
                {project.project_type && (
                  <ProjectInfoItem>
                    <ProjectInfoLabel>Project Type</ProjectInfoLabel>
                    <ProjectInfoValue>{project.project_type}</ProjectInfoValue>
                  </ProjectInfoItem>
                )}
                {project.delivery_address && (
                  <ProjectInfoItem>
                    <ProjectInfoLabel>Delivery Address</ProjectInfoLabel>
                    <ProjectInfoValue>{project.delivery_address}</ProjectInfoValue>
                  </ProjectInfoItem>
                )}
              </ProjectInfoList>
            </Card>
          </RightColumn>
        </ContentGrid>
      </Main>
    </ProjectDetailContainer>
  );
};

export default ProjectDetailPage; 